'use client'

import dynamic from 'next/dynamic'
import { Container, Grid, Alert } from '@mui/material'

// Types
import type { ExploreProps } from '@/types/components/pages/explore'
import type { CardItem } from '@/types/components/molecules/cards'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Utils
import TransText from '@/utils/trans-text'

// Components
const SliderOrganism = dynamic(() => import('@/components/organisms/slider'))
const SearchFormOrganism = dynamic(() => import('@/components/organisms/forms/search'))
const SlideshowBoxCardMolecule = dynamic(() => import('@/components/molecules/cards/slideshow-box'))
const CardMolecule = dynamic(() => import('@/components/molecules/cards'))
const PaginationMolecule = dynamic(() => import('@/components/molecules/pagination'))
const TextAtom = dynamic(() => import('@/components/atoms/text'))

const ExplorePage = (props: ExploreProps) => {
	// Props
	const { lng, image, articles, limit, page, count, qParam, orderParam, sortParam, form } = props

	// Variables
	const { t } = useTranslation(lng)
	const totalPage = Math.ceil(count / limit)

	const cards: CardItem[] = articles.map(article => {
		const fv = article.fields_values ?? []

		return {
			title: article.title,
			image: fv && fv.length > 2 && fv[2].value ? fv[2].value : article.image,
			link: `/article/${article.alias}`,
			year: fv && fv.length > 0 && fv[0].value ? fv[0].value : undefined,
			subtitle: fv && fv.length > 1 && fv[1].value ? fv[1].value : undefined,
			description: fv && fv.length > 5 && fv[5].value ? fv[5].value : undefined,
			coverImage: article.image
		}
	})

	// Box
	const fv = image?.fields_values ?? []
	let box: CardItem = {}
	let finalDescription = TransText('')

	if (Array.isArray(fv) && fv.length > 5) {
		box = {
			title: image?.title ?? '',
			image: fv[0].value,
			link: fv[1].value,
			year: fv[2].value,
			subtitle: fv[3].value,
			description: fv[4].value
		}

		finalDescription = TransText(fv[5].value || '')
	}

	return (
		<Grid container spacing={3}>
			{image && (
				<SliderOrganism lng={lng} title={image.title} image={`/api/download${box.image}`} form={form}>
			<Grid
				width={{ md: '30%' }}
				maxWidth={{ xs: '50%' }}
				position="absolute"
				bottom={{ xs: '50%', md: 60 }}
				left={{ xs: '50%', md: '80%' }}
				zIndex={1}
				sx={{ transform: { xs: 'translate(-50%, 50%)', md: 'translateX(-50%)' } }}
			>
				<SlideshowBoxCardMolecule lng={lng} {...box} />
			</Grid>
		</SliderOrganism>
	)
}

<Grid size={12}>
	<Container>
		<Grid id="search" container spacing={3}>
			{finalDescription.text && (
				<Grid size={12}>
					<TextAtom>{finalDescription.text}</TextAtom>
				</Grid>
			)}

			<SearchFormOrganism
				lng={lng}
				defaultValues={{
					q: qParam,
					order: orderParam,
					sort: sortParam
				}}
			/>

			{cards.length ? (
				<Grid container alignItems="stretch" spacing={2} width={1}>
					{cards.map((card, index) => {
						if (index < 2) return <CardMolecule key={index} lng={lng} {...card} size="medium" />
						else return null
					})}
				</Grid>
			) : null}

			{cards.length > 2 ? (
				<Grid container alignItems="stretch" spacing={2} width={1}>
					{cards.map((card, index) => {
						if (index > 1 && index < 5) return <CardMolecule key={index} lng={lng} {...card} size="small" />
						else return null
					})}
				</Grid>
			) : null}

			{cards.length > 5 ? (
				<Grid container alignItems="stretch" spacing={2} width={1}>
					<CardMolecule lng={lng} {...cards[5]} size="large" />
				</Grid>
			) : null}

			{cards.length > 6 ? (
				<Grid container alignItems="stretch" spacing={2} width={1}>
					{cards.map((card, index) => {
						if (index > 5 && index < 8) return <CardMolecule key={index} lng={lng} {...card} size="medium" />
						else return null
					})}
				</Grid>
			) : null}

			{cards.length > 8 ? (
				<Grid container alignItems="stretch" spacing={2} width={1}>
					<CardMolecule lng={lng} {...cards[8]} size="large" />
				</Grid>
			) : null}

			{cards.length > 9 ? (
				<Grid container alignItems="stretch" spacing={2} width={1}>
					{cards.map((card, index) => {
						if (index > 8 && index < 12) return <CardMolecule key={index} lng={lng} {...card} size="small" />
						else return null
					})}
				</Grid>
			) : null}

			{cards.length > 12 ? (
				<Grid container alignItems="stretch" spacing={2} width={1}>
					{cards.map((card, index) => {
						if (index > 11) return <CardMolecule key={index} lng={lng} {...card} size="medium" />
						else return null
					})}
				</Grid>
			) : null}
		</Grid>
	</Container>
</Grid>

{
	cards.length === 0 ? (
		<Grid size={12}>
			<Container>
				<Alert severity="warning">{t('common:description.notFoundItems')}</Alert>
			</Container>
		</Grid>
	) : null
}

<Grid size={12}>
	<Container>
		{totalPage > 1 && (
			<Grid container justifyContent="center" spacing={2}>
				<PaginationMolecule page={page} count={totalPage} />
			</Grid>
		)}
	</Container>
</Grid>
		</Grid >
	)
}

export default ExplorePage
